import { Button, Menu, MenuItem, Select, SelectChangeEvent, Table, Typography, Flex } from "flux";
import useBatches from "@adora/shared/hooks/use-batches";
import { isBatchComplete, Batch, BatchStatus, BatchType } from "@adora/shared/types/batch";
import { getLocaleTimeString } from "@adora/shared/utils/date";
import useBrands from "../hooks/use-brands";
import { useMemo, useState } from "react";
import { enumValues } from "@adora/shared/utils/enum";
import useUsers from "../hooks/use-users";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import LoraTrainingDialog from "../components/lora-training-dialog";
import ViewLoraParamsDialog from "../components/lora-training-dialog/params";
import { downloadCaptions } from "../utils/captions";
import Page from "@adora/shared/components/layout/page";

export interface Filters {
  brand: number;
  type: BatchType | null;
  creator: number;
}

interface MenuAction {
  label: string;
  onClick: () => void;
  visible?: (batch?: Batch) => boolean;
}

export type BatchFilterType = "brand" | "type" | "creator";

const Batches: React.FC = () => {
  const [filters, setFilters] = useState<Filters>({ brand: 0, type: null, creator: 0 });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBatch, setSelectedBatch] = useState<Batch | undefined>();
  const { openDialog } = useDialog();

  const batchApiFilters = useMemo(() => {
    return {
      ...(filters.brand && { brandId: filters.brand }),
      ...(filters.type && { type: filters.type }),
      ...(filters.creator && { creatorId: filters.creator })
    };
  }, [filters]);

  const { batches, isLoading, invalidateBatchCache } = useBatches(batchApiFilters);
  const { brands } = useBrands();
  const { users } = useUsers();

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, batchId: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    const batch = batches.find((b) => b.id === batchId);
    setSelectedBatch(batch);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBatch(undefined);
  };

  const handleLoraTraining = async () => {
    if (selectedBatch) {
      handleMenuClose();
      const confirm = await openDialog(LoraTrainingDialog, { batch: selectedBatch, brandId: selectedBatch.brandId });
      if (confirm) {
        invalidateBatchCache();
      }
    }
  };

  const handleViewLoraParams = async () => {
    if (selectedBatch) {
      handleMenuClose();
      const confirm = await openDialog(ViewLoraParamsDialog, { batch: selectedBatch });
      if (confirm) {
        invalidateBatchCache();
      }
    }
  };

  const handleCaptionTexts = async () => {
    if (selectedBatch) {
      handleMenuClose();
      const blob = await downloadCaptions(selectedBatch.id);
      const fileURL = window.URL.createObjectURL(blob);

      const alink = document.createElement("a");
      alink.href = fileURL;
      alink.download = "captions_txt.zip";
      alink.click();
    }
  };

  const menuActions: MenuAction[] = [
    {
      label: "Launch Lora Training",
      onClick: handleLoraTraining,
      visible: (batch) => batch?.type === BatchType.DatasetCaptioning
    },
    {
      label: "Download Caption Texts",
      onClick: handleCaptionTexts,
      visible: (batch) => batch?.type === BatchType.DatasetCaptioning
    },
    {
      label: "View LoRA Training Params",
      onClick: handleViewLoraParams,
      visible: (batch) => batch?.type === BatchType.LoraTraining
    },
    {
      label: "View Details",
      onClick: handleViewLoraParams,
      visible: (batch) => batch?.type !== BatchType.LoraTraining
    }
  ];

  const columns = [
    { field: "id", value: "ID", width: 100 },
    {
      field: "brand",
      value: "Brand",
      width: 250,
      renderCell: (row: Batch) => {
        const brandId = row.brandId;
        const brand = brands?.find((b) => b.id === brandId);
        return brand?.name;
      }
    },
    {
      field: "createdBy",
      value: "Created By",
      width: 300,
      renderCell: (row: Batch) => {
        if (users) {
          return users.find((u) => u.id === row.creatorId)?.name;
        }
        return undefined;
      }
    },
    { field: "type", value: "Type", width: 250 },
    { field: "count", value: "Num of Images", width: 150 },
    {
      field: "concept",
      value: "Concept ID",
      width: 150,
      renderCell: (row: Batch) => row.concept?.name
    },
    {
      field: "createdAt",
      value: "Created At",
      width: 150,
      renderCell: (row: Batch) => getLocaleTimeString(row.createdAt)
    },
    {
      field: "updatedAt",
      value: "Duration",
      width: 150,
      renderCell: (row: Batch) => {
        const createdAt = new Date(row.createdAt).getTime();
        const updatedAt = new Date(row.updatedAt).getTime();
        const duration = (updatedAt - createdAt) / 1000;
        return isBatchComplete(row.status) ? `${duration} seconds` : null;
      }
    },
    {
      field: "status",
      value: "Status",
      width: 150,
      renderCell: (row: Batch) => {
        let color = "";
        switch (row.status) {
          case BatchStatus.Failed: {
            color = "error";
            break;
          }
          case BatchStatus.PartialSuccess: {
            color = "warning";
            break;
          }
          default: {
            break;
          }
        }
        return <Typography color={color}>{row.status}</Typography>;
      }
    },
    {
      field: "actions",
      value: "Actions",
      width: 50,
      renderCell: (row: Batch) => {
        return (isBatchComplete(row.status) && row.type === BatchType.DatasetCaptioning) ||
          row.type === BatchType.LoraTraining ? (
          <Button
            variant="text"
            color="secondary"
            size="medium"
            onClick={(e) => handleMenuOpen(e, row.id)}
            dataDogIdentifier="batches page ellipsis action"
          >
            …
          </Button>
        ) : null;
      }
    }
  ] as const;

  const handleFilterChange = (type: BatchFilterType, e: SelectChangeEvent<unknown>) => {
    setFilters({ ...filters, [type]: e.target.value });
  };

  return (
    <Page title="Batches">
      <Flex gap="8px" pb="8px">
        <Flex alignItems="center">
          <Typography variant="subtitle1">Filters:</Typography>
        </Flex>
        <Select
          type="text"
          name="brand"
          value={filters.brand || 0}
          size="medium"
          onChange={(e) => handleFilterChange("brand", e)}
        >
          <MenuItem key={0} value={0} size="medium">
            All Brands
          </MenuItem>
          {brands &&
            brands.map((brand) => (
              <MenuItem key={brand.id} value={brand.id} size="medium">
                {brand.name}
              </MenuItem>
            ))}
        </Select>
        <Select
          type="text"
          name="type"
          value={filters.type || 0}
          size="medium"
          onChange={(e) => handleFilterChange("type", e)}
        >
          <MenuItem key={0} value={0} size="medium">
            All Job Types
          </MenuItem>
          {enumValues(BatchType).map((value) => (
            <MenuItem key={value} value={value} size="medium">
              {value}
            </MenuItem>
          ))}
        </Select>
        <Select
          type="text"
          name="creator"
          value={filters.creator || 0}
          size="medium"
          onChange={(e) => handleFilterChange("creator", e)}
        >
          <MenuItem key={0} value={0} size="medium">
            All users
          </MenuItem>
          {users &&
            users.map((user) => (
              <MenuItem key={user.id} value={user.id} size="medium">
                {user.name}
              </MenuItem>
            ))}
        </Select>
      </Flex>
      <Flex>
        <Table
          columns={columns}
          rows={batches} // TypeScript will verify Batch can be used as a row
          isLoading={isLoading}
          dataDogIdentifier="internal batches page "
        />
      </Flex>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "110px"
          }
        }}
      >
        {menuActions
          .filter((action) => action.visible!(selectedBatch))
          .map((action) => (
            <MenuItem key={action.label} onClick={action.onClick} size="medium">
              {action.label}
            </MenuItem>
          ))}
      </Menu>
    </Page>
  );
};

export default Batches;
